import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { validateForm, validation } from '../../../utils/validation';
import { airlineApi, airplaneApi, airportApi } from '../../../services/apis';
import {
  SearchableSelect,
  Input,
  Dropdown,
  ValidationMessage,
} from '../../../component';

const FlightTransportationFields = ({
  origin = {
    countryCode: '',
    code: '',
  },
  destination = { countryCode: '', code: '' },
  intl = {},
  language = { name: 'en', direction: 'ltr' },
  transportationData = {},
  setFieldValid = () => {},
  setToastify = () => {},
  onChange = () => {},
}) => {
  const [airline, setAirline] = useState({});
  const [airplane, setAirplane] = useState({});
  const [originTerminal, setOriginTerminal] = useState({});
  const [arrivalTerminal, setArrivalTerminal] = useState({});
  const [flightNumber, setFlightNumber] = useState('');
  const [errors, setErrors] = useState({
    airline: [],
    airplane: [],
    originTerminal: [],
    arrivalTerminal: [],
    flightNumber: [],
  });
  const [airlineList, setAirlineList] = useState([]);
  const [airplaneList, setAirplaneList] = useState([]);
  const [originAirportList, setOriginAirportList] = useState([]);
  const [destinationAirportList, setDestinationAirportList] = useState([]);

  // start life cycle
  useEffect(() => {
    fetchAirlines({ target: {} });
    fetchAirplanes({ target: {} });
  }, []);
  useEffect(() => {
    const {
      originTerminal,
      arrivalTerminal,
      transportProvider,
      vehicleName,
      flightNumber,
    } = transportationData;
    setAirline(transportProvider);
    setAirplane(vehicleName);
    setOriginTerminal(originTerminal);
    setArrivalTerminal(arrivalTerminal);
    setFlightNumber(flightNumber);
    setErrors({
      airline: [],
      airplane: [],
      originTerminal: [],
      arrivalTerminal: [],
      flightNumber: [],
    });
  }, [transportationData]);
  useEffect(() => {
    if (origin?.countryCode) fetchOriginAirports();
  }, [origin]);
  useEffect(() => {
    if (destination?.countryCode) fetchDestinationAirports();
  }, [destination]);
  useEffect(() => {
    const { result, error } = handleValidation();
    if (result) onChange(generateFlightDataObject());
    setFieldValid(result);
  }, [airline, airplane, originTerminal, arrivalTerminal, flightNumber]);
  // end life cycle

  // start fetch api
  const fetchAirlines = async ({ target }) => {
    const phrase = target.value || '';
    try {
      const {
        payload: { data },
      } = await airlineApi.searchByPhrase({
        data: { phrase },
      });
      setAirlineList(data);
    } catch ({ message }) {
      setToastify({ type: 'danger', message });
    }
  };
  const fetchAirplanes = async ({ target }) => {
    const phrase = target?.value || '';
    try {
      const {
        payload: { data },
      } = await airplaneApi?.getAll({
        itemPerPage: 100,
        pageNumber: 1,
        data: { filter: { locationCode: phrase }, sort: { code: 'asc' } },
      });
      setAirplaneList(data);
    } catch ({ message }) {
      setToastify({ type: 'danger', message });
    }
  };
  const fetchOriginAirports = async () => {
    try {
      const {
        payload: { data },
      } = await airportApi?.getAll({
        itemPerPage: 100,
        pageNumber: 1,
        data: {
          filter: {
            phrase: origin?.code,
            countryCode: origin?.countryCode,
            type: 'A',
          },
          sort: { ['name.en']: 'asc' },
        },
      });
      setOriginAirportList(data);
    } catch ({ message }) {
      setToastify({ type: 'danger', message });
    }
  };
  const fetchDestinationAirports = async () => {
    try {
      const {
        payload: { data },
      } = await airportApi?.getAll({
        itemPerPage: 100,
        pageNumber: 1,
        data: {
          filter: {
            phrase: destination?.code,
            countryCode: destination?.countryCode,
            type: 'A',
          },
          sort: { ['name.en']: 'asc' },
        },
      });
      setDestinationAirportList(data);
    } catch ({ message }) {
      setToastify({ type: 'danger', message });
    }
  };
  // start fetch api

  // start others
  const handleValidation = () => {
    const { error, result } = validateForm({
      originTerminal: validation?.required(originTerminal?._id).errors,
      arrivalTerminal: validation?.required(arrivalTerminal?._id).errors,
      transportProvider: validation?.required(airline?._id).errors,
      vehicleName: validation?.required(airplane?._id).errors,
      flightNumber: validation?.flightNumber(flightNumber)?.required().errors,
    });
    return { error, result };
  };
  const generateFlightDataObject = () => {
    return {
      originTerminal,
      arrivalTerminal,
      transportProvider: airline,
      vehicleName: airplane,
      flightNumber,
    };
  };
  return (
    <>
      <fieldset className="col-sm-6 col-12 mt-3">
        <SearchableSelect
          name="airline"
          placeholder={intl.formatMessage({
            id: 'profile.airlinename',
          })}
          list={airlineList}
          display={`name.${language.name}`}
          type="airline"
          isLocalization={true}
          search={(event) => fetchAirlines(event)}
          select={({ name, _id }) => {
            setAirline({ name, _id });
            setErrors((error) => {
              return {
                ...error,
                airline: validation?.required(_id)?.errors,
              };
            });
          }}
          defaultValue={airline?.name?.[language.name]}
          shouldBeFilled={true}
          containerClass="w-100"
          labelColor="text-dark"
          inputClassName="py-1 font-smaller"
          heightSize={'indropDown px-lg-0 px-3'}
          listClass="default-border-bottom-radius-small bg-third-light border-color-light-primary"
        />
        <ValidationMessage errors={errors.airline} />
      </fieldset>
      <fieldset className="col-sm-6 col-12 mt-3">
        <SearchableSelect
          name="airplane"
          placeholder={intl.formatMessage({
            id: 'booking.airplanetype',
          })}
          list={airplaneList}
          display={`name.${language.name}`}
          type="airplane"
          isLocalization={true}
          search={(event) => fetchAirplanes(event)}
          select={({ name, _id }) => {
            setAirplane({ name, _id });
            setErrors((error) => {
              return {
                ...error,
                airplane: validation?.required(_id)?.errors,
              };
            });
          }}
          defaultValue={airplane?.name?.[language.name]}
          shouldBeFilled={true}
          containerClass="w-100"
          labelColor="text-dark"
          inputClassName="py-1 font-smaller"
          heightSize={'indropDown px-lg-0 px-3'}
          listClass="default-border-bottom-radius-small bg-third-light border-color-light-primary"
        />
        <ValidationMessage errors={errors.airline} />
      </fieldset>
      <fieldset className="col-sm-6 col-12 mt-3">
        <Dropdown
          direction="rtl"
          name="originTerminal"
          id="originTerminal"
          label="tour.originairport"
          classNames="bg-third-light"
          outSideLabel={true}
          value={originTerminal}
          options={{
            data: originAirportList,
            label: `name.${language.name}`,
            value: '_id',
          }}
          customeStyle={{
            directtion: 'rtl',
          }}
          onChange={({ currentTarget: { valueObject } }) => {
            const { name, _id } = valueObject;
            setOriginTerminal({ name, _id });
            setErrors((error) => {
              return {
                ...error,
                originTerminal: validation?.required(_id)?.errors,
              };
            });
          }}
        />
        <ValidationMessage errors={errors.originTerminal} />
      </fieldset>
      <fieldset className="col-sm-6 col-12 mt-3">
        <Dropdown
          direction="rtl"
          name="arrivalTerminal"
          id="arrivalTerminal"
          label="tour.destinationairport"
          classNames="bg-third-light"
          outSideLabel={true}
          value={arrivalTerminal}
          options={{
            data: destinationAirportList,
            label: `name.${language.name}`,
            value: '_id',
          }}
          customeStyle={{
            directtion: 'rtl',
          }}
          onChange={({ currentTarget: { valueObject } }) => {
            const { name, _id } = valueObject;
            setArrivalTerminal({ name, _id });
            setErrors((error) => {
              return {
                ...error,
                arrivalTerminal: validation?.required(_id)?.errors,
              };
            });
          }}
        />
        <ValidationMessage errors={errors.arrivalTerminal} />
      </fieldset>
      <fieldset className="col-sm-6 col-12 mt-3">
        <Input
          name="flightnumber"
          label={intl.formatMessage({
            id: 'booking.flightno',
          })}
          value={flightNumber}
          onChange={({ target: { value } }) => {
            setFlightNumber(value),
              setErrors((errors) => {
                return {
                  ...errors,
                  flightNumber: validation?.flightNumber(value)?.required()
                    .errors,
                };
              });
          }}
          outSideLabelClass="text-start"
          outSideLabelPosition="top"
          outSideLabel={true}
          themeMode="light"
        />
        <ValidationMessage errors={errors.flightNumber} />
      </fieldset>
    </>
  );
};

FlightTransportationFields.propTypes = {
  originCountryCode: PropTypes?.string,
  destinationCountryCode: PropTypes?.string,
  intl: PropTypes.object,
  language: PropTypes.object,
  transportationData: PropTypes.object,
  setFieldValid: PropTypes.func,
  setToastify: PropTypes.func,
  onChange: PropTypes.func,
};

export default FlightTransportationFields;
